<template>
    <div ref="trigger" class="variation-cross-selling-trigger">
        <section v-if="isVisible" class="row product-slider variation-cross-selling" :class="{ 'is-dragging': isDragging }">
            <h3 class="h3">
                {{ $translate("biokinderDesign::Template.itemCrossSellingHeading") }}
            </h3>

            <div class="product-slider-container position-relative" :class="{ 'is-loading': loading }">
                <div
                    v-if="loading && renderedItems.length"
                    class="variation-cross-selling-loading position-absolute w-100 h-100 d-flex align-items-center justify-content-center">
                    <loading-animation></loading-animation>
                </div>

                <div v-if="showSkeleton" class="slider-skeleton row no-gutters">
                    <div v-for="index in skeletonItemsCount" :key="index" class="product-slide col-12">
                        <div class="variation-cross-selling-skeleton-card">
                            <div class="skeleton-image">
                                <span class="skeleton-basket"></span>
                            </div>
                            <div class="skeleton-text"></div>
                            <div class="skeleton-text"></div>
                            <div class="skeleton-price"></div>
                        </div>
                    </div>
                </div>

                <ul
                    v-else-if="itemsVisible"
                    ref="track"
                    class="product-slider-slider crossSellingProduct variation-cross-selling__track"
                    @scroll="updateScrollControls"
                    @click.capture="preventClickAfterDrag"
                    @pointerdown="startDrag"
                    @pointermove="drag"
                    @pointerup="stopDrag"
                    @pointercancel="stopDrag"
                    @pointerleave="stopDrag">
                    <li v-for="item in renderedItems" :key="item.variation.id" class="product-slide col-12 variation-cross-selling__item">
                        <category-item
                            :image-url-accessor="'urlSecondPreview'"
                            :item-data="item"
                            :decimal-count="decimalCount"
                            :disable-carousel="true">
                        </category-item>
                    </li>
                </ul>

                <div v-if="renderedItems.length > 1" class="variation-cross-selling__controls">
                    <button type="button" class="variation-cross-selling__control" aria-label="Vorherige Artikel" :disabled="!canScrollPrevious" @click="scrollByPage(-1)">
                        <svg aria-hidden="true" viewBox="0 0 24 24"><path d="m15 18-6-6 6-6"></path></svg>
                    </button>
                    <button type="button" class="variation-cross-selling__control" aria-label="Nächste Artikel" :disabled="!canScrollNext" @click="scrollByPage(1)">
                        <svg aria-hidden="true" viewBox="0 0 24 24"><path d="m9 18 6-6-6-6"></path></svg>
                    </button>
                </div>

            </div>
        </section>
    </div>
</template>

<script>
import ApiService from "../../services/ApiService";

export default {
    name: "variation-cross-selling",

    props: {
        crossSellingIds: {
            type: Array,
            default: () => []
        },
        decimalCount: {
            type: Number,
            default: 0
        }
    },

    data()
    {
        return {
            renderedItems: [],
            loading: false,
            itemsVisible: true,
            isActivated: false,
            isDragging: false,
            isPointerDown: false,
            hasDragged: false,
            dragStartX: 0,
            dragStartScrollLeft: 0,
            suppressNextClick: false,
            canScrollPrevious: false,
            canScrollNext: false,
            resizeObserver: null
        };
    },

    computed: {
        crossSellingKey()
        {
            return (this.crossSellingIds || []).join(",");
        },

        hasCrossSellingIds()
        {
            return (this.crossSellingIds || []).length > 0;
        },

        isVisible()
        {
            return this.isActivated && this.hasCrossSellingIds && (this.loading || this.renderedItems.length > 0);
        },

        showSkeleton()
        {
            return this.loading && this.renderedItems.length === 0;
        },

        skeletonItemsCount()
        {
            return 5;
        }
    },

    created()
    {
        this.itemCache = new Map();
        this.resolvedKeyCache = new Set();
        this.requestTokenCounter = 0;
    },

    mounted()
    {
        this.observeVisibility();
        window.addEventListener("resize", this.updateScrollControls);
    },

    beforeDestroy()
    {
        this.disconnectObserver();
        window.removeEventListener("resize", this.updateScrollControls);

        if (this.resizeObserver)
        {
            this.resizeObserver.disconnect();
        }
    },

    watch: {
        crossSellingKey(newValue, oldValue)
        {
            if (newValue !== oldValue)
            {
                if (this.isActivated)
                {
                    this.syncRenderedItems();
                }
                else
                {
                    this.observeVisibility();
                }
            }
        }
    },

    methods: {
        observeVisibility()
        {
            if (!this.hasCrossSellingIds || this.isActivated)
            {
                return;
            }

            if (typeof window === "undefined" || typeof IntersectionObserver === "undefined")
            {
                this.activate();
                return;
            }

            this.$nextTick(() =>
            {
                if (this.isActivated || !this.$refs.trigger)
                {
                    return;
                }

                this.disconnectObserver();
                this.visibilityObserver = new IntersectionObserver(entries =>
                {
                    if (entries.some(entry => entry.isIntersecting))
                    {
                        this.activate();
                    }
                }, {
                    rootMargin: "0px 0px 400px 0px",
                    threshold: 0
                });
                this.visibilityObserver.observe(this.$refs.trigger);
            });
        },

        activate()
        {
            if (this.isActivated)
            {
                return;
            }

            this.isActivated = true;
            this.disconnectObserver();
            this.syncRenderedItems();
        },

        disconnectObserver()
        {
            if (this.visibilityObserver)
            {
                this.visibilityObserver.disconnect();
                this.visibilityObserver = null;
            }
        },

        syncRenderedItems(allowFetch = true)
        {
            const variationIds = this.crossSellingIds || [];
            const key = variationIds.join(",");

            if (!variationIds.length)
            {
                this.replaceRenderedItems([]);
                return;
            }

            const missingIds = variationIds.filter(variationId => !this.itemCache.has(variationId));

            if (!missingIds.length || this.resolvedKeyCache.has(key))
            {
                this.resolvedKeyCache.add(key);

                this.setVariationItems(variationIds);
                return;
            }

            this.fetchMissingItems(missingIds, key)
                .finally(() =>
                {
                    this.setVariationItems(variationIds);
                });
        },

        fetchMissingItems(missingIds, key)
        {
            const token = ++this.requestTokenCounter;
            this.loading = true;

            return new Promise(resolve =>
            {
                ApiService.get("/rest/io/variations", {
                    variationIds: missingIds,
                    resultFieldTemplate: "ItemCrossSellingVertical"
                })
                    .done(response =>
                    {
                        if (token !== this.requestTokenCounter)
                        {
                            resolve();
                            return;
                        }

                        const items = (response.documents || []).map(document => document.data);
                        this.seedCache(items);
                        this.resolvedKeyCache.add(key);
                        resolve();
                    })
                    .fail(() =>
                    {
                        resolve();
                    })
                    .always(() =>
                    {
                        if (token === this.requestTokenCounter)
                        {
                            this.loading = false;
                        }
                    });
            });
        },

        setVariationItems(variationIds)
        {
            this.replaceRenderedItems(this.filterRenderableItems(
                variationIds
                    .map(variationId => this.itemCache.get(variationId))
                    .filter(item => !!item)
            ).slice(0, 8));
        },

        seedCache(items)
        {
            (items || []).forEach(item =>
            {
                const variationId = parseInt(item?.variation?.id, 10);

                if (variationId > 0)
                {
                    this.itemCache.set(variationId, item);
                }
            });
        },

        filterRenderableItems(items)
        {
            return (items || []).filter(item =>
            {
                return !!item &&
                    !!item.variation &&
                    parseInt(item.variation.id, 10) > 0 &&
                    !!item.images &&
                    !!item.prices &&
                    !!item.prices.default &&
                    !!item.prices.default.price &&
                    item.prices.default.price.value > 0;
            });
        },

        replaceRenderedItems(items)
        {
            this.renderedItems = items;
            this.itemsVisible = items.length > 0;
            this.$nextTick(this.observeTrackSize);
        },

        observeTrackSize()
        {
            const track = this.$refs.track;

            if (!track) return;

            if (typeof ResizeObserver !== "undefined" && !this.resizeObserver)
            {
                this.resizeObserver = new ResizeObserver(this.updateScrollControls);
                this.resizeObserver.observe(track);
            }

            this.updateScrollControls();
        },

        updateScrollControls()
        {
            const track = this.$refs.track;

            if (!track)
            {
                this.canScrollPrevious = false;
                this.canScrollNext = false;
                return;
            }

            const maximumScrollLeft = Math.max(0, track.scrollWidth - track.clientWidth);
            const edgeTolerance = 1;

            this.canScrollPrevious = track.scrollLeft > edgeTolerance;
            this.canScrollNext = track.scrollLeft < maximumScrollLeft - edgeTolerance;
        },

        /** Scrolls most of the visible cross-selling track in the chosen direction. */
        scrollByPage(direction)
        {
            const track = this.$refs.track;

            if (!track) return;

            track.scrollBy({ left: direction * track.clientWidth * 0.8, behavior: "smooth" });
        },

        /** Records mouse/pen input; capture starts only after a real drag. */
        startDrag(event)
        {
            if (event.pointerType === "touch" || event.target.closest("button")) return;

            this.isPointerDown = true;
            this.hasDragged = false;
            this.dragStartX = event.clientX;
            this.dragStartScrollLeft = this.$refs.track.scrollLeft;
        },

        /** Starts a drag only after movement, so links retain their native click behavior. */
        drag(event)
        {
            if (!this.isPointerDown) return;

            const movement = event.clientX - this.dragStartX;

            if (!this.hasDragged && Math.abs(movement) < 5) return;

            if (!this.hasDragged)
            {
                this.hasDragged = true;
                this.isDragging = true;
                this.$refs.track.setPointerCapture(event.pointerId);
            }

            event.preventDefault();
            this.$refs.track.scrollLeft = this.dragStartScrollLeft - movement;
        },

        /** Ends a custom drag and releases its pointer capture. */
        stopDrag(event)
        {
            if (!this.isPointerDown) return;

            this.isPointerDown = false;
            this.isDragging = false;

            if (this.$refs.track.hasPointerCapture(event.pointerId))
            {
                this.$refs.track.releasePointerCapture(event.pointerId);
            }
            
            if (this.hasDragged)
            {
                this.suppressNextClick = true;
                window.setTimeout(() => { this.suppressNextClick = false; }, 0);
            }

            this.hasDragged = false;
        },

        /** Prevents the browser's trailing click after an actual pointer drag. */
        preventClickAfterDrag(event)
        {
            if (!this.suppressNextClick) return;

            event.preventDefault();
            event.stopPropagation();
            this.suppressNextClick = false;
        }
    }
}
</script>
